import { useEffect, useRef, useState } from 'react';
import type { DxmCategoryNodeDTO, DxmCategorySearchResultDTO } from '@wberp/shared';
import { api } from './api';
import './category-picker.css';

type CategoryLevel = {
  parent: DxmCategoryNodeDTO | null;
  items: DxmCategoryNodeDTO[];
  loading: boolean;
  error: string | null;
};

type CategoryPickerProps = {
  initialOpen?: boolean;
  value?: number;
  selectedPath: DxmCategoryNodeDTO[];
  onChange: (category: DxmCategoryNodeDTO | undefined, path: DxmCategoryNodeDTO[]) => void;
};

function categoryLabel(category: DxmCategoryNodeDTO): string {
  return category.categoryNameZh ? `${category.categoryNameZh} · ${category.categoryName}` : category.categoryName;
}

export function CategoryPicker({ value, selectedPath, onChange, initialOpen = false }: CategoryPickerProps) {
  const [open, setOpen] = useState(initialOpen);
  const [path, setPath] = useState<DxmCategoryNodeDTO[]>(selectedPath);
  const [levels, setLevels] = useState<CategoryLevel[]>([]);
  const [search, setSearch] = useState('');
  const [searchResult, setSearchResult] = useState<DxmCategorySearchResultDTO | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchRefresh, setSearchRefresh] = useState(0);
  const cache = useRef(new Map<number | 'root', DxmCategoryNodeDTO[]>());
  const requestId = useRef(0);

  useEffect(() => () => { requestId.current += 1; }, []);

  useEffect(() => {
    if (!initialOpen) return;
    const version = ++requestId.current;
    let active = true;
    setLevels([{ parent: null, items: [], loading: true, error: null }]);
    api.getDxmCategories().then((result) => {
      if (!active || version !== requestId.current) return;
      cache.current.set('root', result.items);
      setLevels([{ parent: null, items: result.items, loading: false, error: null }]);
    }).catch((reason: unknown) => {
      if (active && version === requestId.current) setLevels([{ parent: null, items: [], loading: false, error: reason instanceof Error ? reason.message : '类目读取失败' }]);
    });
    return () => { active = false; };
  }, [initialOpen]);

  useEffect(() => {
    const query = search.trim();
    setSearchResult(null);
    setSearchError(null);
    if (!open || !query) { setSearchLoading(false); return; }
    let active = true;
    setSearchLoading(true);
    const timer = setTimeout(() => {
      api.searchDxmCategories(query).then((result) => {
        if (active) setSearchResult(result);
      }).catch((reason: unknown) => {
        if (active) setSearchError(reason instanceof Error ? reason.message : '搜索类目失败');
      }).finally(() => { if (active) setSearchLoading(false); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [search, open, searchRefresh]);

  async function showChildren(parent: DxmCategoryNodeDTO | null, ancestors: DxmCategoryNodeDTO[], preceding: CategoryLevel[]): Promise<void> {
    const id = ++requestId.current;
    const cacheKey = parent?.categoryId ?? 'root';
    const cached = cache.current.get(cacheKey);
    setOpen(true);
    setPath(ancestors);
    onChange(undefined, ancestors);
    const nextLevel: CategoryLevel = { parent, items: cached ?? [], loading: !cached, error: null };
    setLevels([...preceding, nextLevel]);
    if (cached) return;
    try {
      const result = await api.getDxmCategories(parent?.categoryId);
      if (id !== requestId.current) return;
      cache.current.set(cacheKey, result.items);
      setLevels([...preceding, { ...nextLevel, items: result.items, loading: false }]);
    } catch (reason) {
      if (id !== requestId.current) return;
      setLevels([...preceding, { ...nextLevel, loading: false, error: reason instanceof Error ? reason.message : '类目读取失败' }]);
    }
  }

  function choose(category: DxmCategoryNodeDTO, levelIndex: number): void {
    const nextPath = [...path.slice(0, levelIndex), category];
    const preceding = levels.slice(0, levelIndex + 1);
    if (!category.isLeaf) {
      void showChildren(category, nextPath, preceding);
      return;
    }
    requestId.current += 1;
    setPath(nextPath);
    setLevels(preceding);
    onChange(category, nextPath);
  }

  function clear(): void {
    requestId.current += 1;
    setOpen(false);
    setPath([]);
    setLevels([]);
    setSearch('');
    onChange(undefined, []);
  }

  function chooseSearchResult(result: DxmCategorySearchResultDTO['items'][number]): void {
    setSearch('');
    const preceding = result.path.map((category, index) => ({
      parent: result.path[index - 1] ?? null,
      items: cache.current.get(result.path[index - 1]?.categoryId ?? 'root') ?? [category],
      loading: false,
      error: null,
    }));
    if (!result.category.isLeaf) {
      void showChildren(result.category, result.path, preceding);
      return;
    }
    requestId.current += 1;
    setPath(result.path);
    setLevels(preceding);
    onChange(result.category, result.path);
  }

  const firstVisible = Math.max(0, levels.length - 2);
  const selected = value === undefined ? undefined : path.find(category => category.categoryId === value);
  return <section className="category-picker" aria-label="WB 类目选择">
    <div className="category-picker-toolbar">
      <div className="category-picker-title">WB 类目 <span>可选，留空由 AI 选择</span></div>
      <div className="category-picker-actions">
        {open || path.length > 0 ? <button type="button" className="button button-small" onClick={clear}>使用 AI 选择</button> : null}
        <button type="button" className="button button-small" onClick={() => {
          if (!levels.length) void showChildren(null, [], []);
          else setOpen(current => !current);
        }}>{open ? '收起类目' : selected ? '修改类目' : '手动选择类目'}</button>
      </div>
    </div>
    {open && <>
      <div className="category-picker-search">
        <input className="input" aria-label="搜索 WB 类目" placeholder="搜索全部类目：中文、俄文或编号" maxLength={100} value={search} onChange={(event) => setSearch(event.currentTarget.value)} onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }} />
        {search && <button type="button" className="button button-small button-quiet" onClick={() => setSearch('')}>清除搜索</button>}
      </div>
      <nav className="category-picker-path" aria-label="类目路径">
        <button type="button" onClick={() => void showChildren(null, [], [])}>全部类目</button>
        {path.map((category, index) => <span key={category.categoryId}>
          <span className="category-path-separator" aria-hidden="true">/</span>
          <button type="button" title={categoryLabel(category)} aria-current={index === path.length - 1 ? 'location' : undefined}
            onClick={() => choose(category, index)}>{category.categoryNameZh || category.categoryName}</button>
        </span>)}
      </nav>
      {search.trim() ? <div className="category-picker-search-results" aria-label="类目搜索结果" aria-busy={searchLoading}>
        {searchLoading ? <div className="category-picker-message" role="status"><span className="spinner" />正在搜索全部类目，首次搜索需读取类目目录…</div>
          : searchError ? <div className="category-picker-message category-picker-error" role="alert"><span>{searchError}</span><button type="button" className="button button-small" onClick={() => setSearchRefresh((value) => value + 1)}>重试搜索</button></div>
          : searchResult && searchResult.items.length === 0 ? <div className="category-picker-message">没有找到匹配的类目，请更换中文、俄文名称或编号。</div>
          : searchResult && <>
            <div className="category-picker-column-head"><span>找到 {searchResult.total} 个类目{searchResult.total > searchResult.items.length ? `，显示前 ${searchResult.items.length} 个` : ''}</span></div>
            {searchResult.items.map((result) => <button type="button" className="category-picker-row category-picker-search-result" key={result.path.map((category) => category.categoryId).join('/')} title={result.path.map(categoryLabel).join(' / ')} onClick={() => chooseSearchResult(result)}>
              <span className="category-picker-search-result-copy"><span className="category-picker-row-name">{result.category.categoryNameZh && <strong>{result.category.categoryNameZh}</strong>}<span>{result.category.categoryName}</span></span><small>{result.path.map((category) => category.categoryNameZh || category.categoryName).join(' / ')}</small></span>
              <span className="category-picker-row-id">{result.category.categoryId}</span><span className="category-picker-row-mark" aria-label={result.category.isLeaf ? '选择该类目' : '查看子类目'}>{result.category.isLeaf ? '✓' : '›'}</span>
            </button>)}
          </>}
      </div> : <div className={`category-picker-columns${levels.length > 1 ? ' category-picker-columns-split' : ''}`}>
        {levels.slice(firstVisible).map((level, visibleIndex) => {
          const levelIndex = firstVisible + visibleIndex;
          const title = level.parent ? `${level.parent.categoryNameZh || level.parent.categoryName} · 子类目` : '主类目';
          return <section className="category-picker-column" key={level.parent?.categoryId ?? 'root'} aria-label={title}>
            <div className="category-picker-column-head" title={title}><span>{title}</span><span>{level.loading ? '读取中' : `${level.items.length} 项`}</span></div>
            <div className="category-picker-list" aria-busy={level.loading}>
              {level.loading ? <div className="category-picker-message" role="status"><span className="spinner" />正在读取类目…</div>
                : level.error ? <div className="category-picker-message category-picker-error" role="alert"><span>{level.error}</span><button type="button" className="button button-small" onClick={() => void showChildren(level.parent, path.slice(0, levelIndex), levels.slice(0, levelIndex))}>重试</button></div>
                : level.items.length === 0 ? <div className="category-picker-message">暂无{level.parent ? '下级' : ''}类目，请返回重选或使用 AI 选择。</div>
                : level.items.map(category => {
                  const active = path[levelIndex]?.categoryId === category.categoryId;
                  return <button type="button" className={`category-picker-row${active ? ' is-active' : ''}`} key={category.categoryId}
                    aria-pressed={active} title={`${categoryLabel(category)} · ${category.categoryId}`} onClick={() => choose(category, levelIndex)}>
                    <span className="category-picker-row-name">{category.categoryNameZh && <strong>{category.categoryNameZh}</strong>}<span>{category.categoryName}</span></span>
                    <span className="category-picker-row-id">{category.categoryId}</span>
                    <span className="category-picker-row-mark" aria-label={category.isLeaf ? '叶子类目' : '查看子类目'}>{category.isLeaf ? value === category.categoryId ? '✓' : '·' : '›'}</span>
                  </button>;
                })}
            </div>
          </section>;
        })}
      </div>}
    </>}
    <div className={`category-picker-selection${selected ? ' has-selection' : ''}`} aria-live="polite">
      {selected ? <><span className="category-picker-selection-mark">✓</span><span title={path.map(categoryLabel).join(' / ')}>已选：{categoryLabel(selected)}</span><span className="category-picker-row-id">{selected.categoryId}</span></>
        : open && path.length ? '继续选择下级类目，选到叶子类目后生效。' : '未指定类目，发布时由 AI 逐级选择。'}
    </div>
  </section>;
}
